import type { ComponentType, CSSProperties } from 'react';
import {
  Brain,
  Broadcast,
  Browser,
  ChartLineUp,
  ChatCircleDots,
  Cloud,
  Code,
  Database,
  Detective,
  Gauge,
  GitBranch,
  Lightning,
  MagicWand,
  PaintBrush,
  Robot,
  Scales,
  ShieldCheck,
  Stack,
  Target,
  UsersThree,
} from '@phosphor-icons/react';

/**
 * TEC-512 editorial-rail redesign: skill icon lookup for `skillsAndTechnologies`, following the
 * exact pattern `BENEFIT_ICONS` proves out in `web-app/components/filters/FilterBuildingBlocks.tsx`.
 * Values are free text an LLM extracts from job postings, not a fixed enum, so this covers as
 * many common values (matched case-insensitively) as reasonably curatable, then falls back to a
 * category bucket by keyword; not one generic icon for everything, since skills span far more
 * categories than benefits do (BENEFIT_ICONS's single `Gift` fallback would be too coarse here).
 * Generic Phosphor outline icons, not real tech logos (simple-icons): consistent with every other
 * icon on the redesigned page, per the design review's explicit call; revisit only if user
 * testing shows people scanning specifically for brand logos.
 */
type SkillIconComponent = ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'; style?: CSSProperties }>;

const SKILL_ICONS: Record<string, SkillIconComponent> = {
  // Languages
  javascript: Code,
  typescript: Code,
  python: Code,
  java: Code,
  'c++': Code,
  'c#': Code,
  go: Code,
  golang: Code,
  rust: Code,
  ruby: Code,
  php: Code,
  kotlin: Code,
  swift: Code,
  scala: Code,
  'c': Code,
  // Cloud / infra
  aws: Cloud,
  'amazon web services': Cloud,
  azure: Cloud,
  gcp: Cloud,
  'google cloud': Cloud,
  kubernetes: Cloud,
  docker: Cloud,
  terraform: Cloud,
  ansible: Cloud,
  jenkins: Cloud,
  'ci/cd': Cloud,
  devops: Cloud,
  grpc: Cloud,
  kafka: Cloud,
  // Databases
  sql: Database,
  postgresql: Database,
  postgres: Database,
  mysql: Database,
  mongodb: Database,
  redis: Database,
  elasticsearch: Database,
  dynamodb: Database,
  nosql: Database,
  // Frontend / product
  react: Browser,
  vue: Browser,
  angular: Browser,
  'next.js': Browser,
  css: Browser,
  html: Browser,
  figma: Browser,
  'ui/ux': Browser,
  'ux design': Browser,
  // Data / analytics
  sql_data: Database,
  'data analysis': ChartLineUp,
  'data science': ChartLineUp,
  'data engineering': ChartLineUp,
  pandas: ChartLineUp,
  spark: ChartLineUp,
  tableau: ChartLineUp,
  'power bi': ChartLineUp,
  // AI / ML
  ml: Brain,
  'machine learning': Brain,
  'deep learning': Brain,
  ai: Brain,
  'artificial intelligence': Brain,
  llm: Brain,
  llms: Brain,
  'large language models': Brain,
  nlp: Brain,
  'natural language processing': Brain,
  'computer vision': Brain,
  'generative ai': Brain,
  genai: Brain,
  tensorflow: Brain,
  pytorch: Brain,
  'reinforcement learning': Brain,
  'fine-tuning': Brain,
  finetuning: Brain,
  prompting: MagicWand,
  'prompt engineering': MagicWand,
  agent: Robot,
  agents: Robot,
  agentic: Robot,
  'agent workflows': Robot,
  retrieval: Detective,
  rag: Detective,
  'retrieval augmented generation': Detective,
  // Systems / infra
  'production systems': Stack,
  'production ml': Stack,
  'distributed systems': Stack,
  microservices: Stack,
  'system design': Stack,
  scalability: Gauge,
  'high availability': Gauge,
  performance: Gauge,
  'performance tuning': Gauge,
  optimization: Gauge,
  'event-driven': Broadcast,
  'pub/sub': Broadcast,
  streaming: Broadcast,
  git: GitBranch,
  github: GitBranch,
  gitlab: GitBranch,
  'version control': GitBranch,
  security: ShieldCheck,
  cybersecurity: ShieldCheck,
  compliance: ShieldCheck,
  encryption: ShieldCheck,
  // Debugging / quality
  debugging: Detective,
  'debugging pipelines': Detective,
  troubleshooting: Detective,
  observability: Detective,
  monitoring: Gauge,
  logging: Detective,
  testing: Target,
  qa: Target,
  'quality assurance': Target,
  'test automation': Target,
  // Design
  'graphic design': PaintBrush,
  'visual design': PaintBrush,
  branding: PaintBrush,
  // Soft skills
  communication: ChatCircleDots,
  'clinical communication': ChatCircleDots,
  'stakeholder communication': ChatCircleDots,
  leadership: UsersThree,
  teamwork: UsersThree,
  collaboration: UsersThree,
  'cross-functional collaboration': UsersThree,
  mentoring: UsersThree,
  agile: UsersThree,
  scrum: UsersThree,
  'project management': UsersThree,
  'problem solving': Lightning,
  negotiation: Scales,
};

const LANGUAGE_KEYWORDS = ['java', 'python', 'script', 'kotlin', 'swift', 'scala', 'ruby', 'rust', 'golang', ' go', 'php', 'c++', 'c#'];
const CLOUD_KEYWORDS = ['aws', 'azure', 'cloud', 'kubernetes', 'docker', 'terraform', 'devops', 'ci/cd', 'infra', 'kafka', 'grpc'];
const DATABASE_KEYWORDS = ['sql', 'database', 'db', 'redis', 'mongo', 'postgres', 'mysql', 'elastic'];
const FRONTEND_KEYWORDS = ['react', 'vue', 'angular', 'frontend', 'front-end', 'css', 'html', 'ui', 'ux', 'figma', 'design'];
const DATA_KEYWORDS = ['data', 'analytics', 'statistics', 'analysis'];
const AI_ML_KEYWORDS = ['ml', 'ai', 'llm', 'model', 'neural', 'nlp', 'vision', 'generative'];
const AGENT_KEYWORDS = ['agent', 'automation', 'workflow'];
const SYSTEMS_KEYWORDS = ['system', 'distributed', 'production', 'infrastructure', 'architecture', 'scalab', 'pipeline'];
const DEBUG_KEYWORDS = ['debug', 'troubleshoot', 'observab', 'incident', 'root cause'];
const SOFT_SKILL_KEYWORDS = ['communicat', 'leadership', 'teamwork', 'collaborat', 'mentor', 'agile', 'scrum', 'management', 'stakeholder'];

function matchesAny(value: string, keywords: string[]): boolean {
  return keywords.some((keyword) => value.includes(keyword));
}

/**
 * Resolves a skill/technology facet value to an icon: exact curated match first (the large
 * SKILL_ICONS map above), then a category-keyword fallback bucket by substring match. Returns
 * null; no icon at all; only for values that genuinely don't fit any bucket, so a chip never
 * gets a meaningless generic glyph; the goal per the design review is real icons wherever a
 * value's category can be inferred, not maximal coverage at the cost of accuracy.
 */
export function getSkillIcon(value: string): SkillIconComponent | null {
  const lower = value.toLowerCase().trim();
  const exact = SKILL_ICONS[lower];
  if (exact) return exact;

  if (matchesAny(lower, LANGUAGE_KEYWORDS)) return Code;
  if (matchesAny(lower, CLOUD_KEYWORDS)) return Cloud;
  if (matchesAny(lower, DATABASE_KEYWORDS)) return Database;
  if (matchesAny(lower, FRONTEND_KEYWORDS)) return Browser;
  if (matchesAny(lower, AI_ML_KEYWORDS)) return Brain;
  if (matchesAny(lower, AGENT_KEYWORDS)) return Robot;
  if (matchesAny(lower, DEBUG_KEYWORDS)) return Detective;
  if (matchesAny(lower, SYSTEMS_KEYWORDS)) return Stack;
  if (matchesAny(lower, DATA_KEYWORDS)) return ChartLineUp;
  if (matchesAny(lower, SOFT_SKILL_KEYWORDS)) return UsersThree;

  return null;
}
